import { FC, forwardRef, KeyboardEvent, MouseEvent, ReactElement, ReactNode } from 'react'

import BugReportIcon from '@mui/icons-material/BugReport'
import { Box, IconButton, ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import { warnDev } from '@/utils/warnDev'

type Props = {
  onClick?: (e?: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void
  icon?: ReactElement
  text: string
  disabled?: boolean
  displaySeparately?: boolean
  tooltip?: { content: ReactNode; show: boolean }
}

/** Component to be used as a base for creating new ActionItems. NOT for standalone use (eg. ActionDeleteItem) */
export const ActionMenuItem: FC<Props> = forwardRef<HTMLLIElement, Props>(
  ({ onClick, icon, text, disabled = false, displaySeparately = false, tooltip }, ref) => {
    if (displaySeparately) {
      if (!icon)
        warnDev(`Icon is required for displaySeparately action menu item. Item text: ${text}`)

      return (
        <ConsoleTooltip title={text} placement="top">
          <IconButton onClick={onClick} disabled={disabled} sx={{ m: 0 }}>
            {icon || <BugReportIcon />}
          </IconButton>
        </ConsoleTooltip>
      )
    }

    return (
      <ConsoleTooltip title={tooltip?.content} disabled={!tooltip?.show}>
        {/* Tootip won't show if MenuItem is disabled, the Box fixes this behaviour */}
        <Box>
          <MenuItem onClick={onClick} ref={ref} disabled={disabled}>
            {icon && <ListItemIcon>{icon}</ListItemIcon>}
            <ListItemText>{text}</ListItemText>
          </MenuItem>
        </Box>
      </ConsoleTooltip>
    )
  }
)

ActionMenuItem.displayName = 'ActionMenuItem'
